import React from "react";
import {
  Question,
  QuestionAnswers,
  InterviewSession,
  InterviewSessionActivityLog,
} from "../../../Interface/Index";
import {
  QUESTION_TYPES,
  SESSION_STATUS,
  VALIDATION_CONSTANTS,
} from "../constants/interview-constants";
import { AIAssessmentQuestion } from "../../../Entities/ai-assessment.entity";
import dayjs from "dayjs";
import {
  FullscreenExitOutlined,
  SendOutlined,
  WarningOutlined,
  ReloadOutlined,
  VideoCameraOutlined,
  BlockOutlined,
  DeleteRowOutlined,
  DisconnectOutlined,
  ClockCircleOutlined,
  FullscreenOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import { getFingerprint } from "@thumbmarkjs/thumbmarkjs";

export const isQuestionAnswered = (
  question: Question,
  questionAnswer: QuestionAnswers
): boolean => {
  if (!question || !questionAnswer) return false;

  const { type } = question;

  if (question.responses && question.responses.length > 0) {
    return true;
  }

  switch (type?.toLowerCase()) {
    case QUESTION_TYPES.FILE_UPLOAD:
      return Boolean(questionAnswer.fileUrl);
    case QUESTION_TYPES.MCQ:
      return Boolean(questionAnswer.answer);
    case QUESTION_TYPES.DESCRIPTIVE:
    case QUESTION_TYPES.CODING:
      return Boolean(
        questionAnswer.answer && questionAnswer.answer.trim().length > 0
      );
    default:
      return Boolean(questionAnswer.answer);
  }
};

export const validateAnswer = (
  question: Question,
  answer: string | null
): boolean => {
  if (!question) return false;

  const { type } = question;

  switch (type?.toLowerCase()) {
    case QUESTION_TYPES.MCQ:
      return Boolean(answer);
    case QUESTION_TYPES.DESCRIPTIVE:
      return Boolean(
        answer && answer.trim().length >= VALIDATION_CONSTANTS.MIN_ANSWER_LENGTH
      );
    case QUESTION_TYPES.CODING:
      return Boolean(
        answer && answer.trim().length >= VALIDATION_CONSTANTS.MIN_ANSWER_LENGTH
      );
    case QUESTION_TYPES.FILE_UPLOAD:
      return Boolean(answer); // fileUrl
    default:
      return Boolean(answer);
  }
};

export const getProgressPercentage = (
  currentIndex: number,
  totalQuestions: number
): number => {
  if (totalQuestions === 0) return 0;
  if (currentIndex < 0) return 0;
  if (currentIndex >= totalQuestions) return 100;

  return Math.round(((currentIndex + 1) / totalQuestions) * 100);
};

export const isSessionLive = (session: InterviewSession): boolean => {
  return Boolean(session?.isLive);
};

export const isSessionCompleted = (session: InterviewSession): boolean => {
  return session?.status !== SESSION_STATUS.PENDING;
};

export const areQuestionsGenerated = (session: InterviewSession): boolean => {
  return Boolean(session?.isQuestionsGenerated);
};

export const getNextUnansweredQuestionIndex = (
  questions: Question[],
  currentAnswers: QuestionAnswers[]
): number => {
  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    const answer = currentAnswers[i];

    if (!isQuestionAnswered(question, answer)) {
      return i;
    }
  }
  return -1;
};

export const getRoundCompletionPercentage = (
  questions: Question[],
  currentAnswers: QuestionAnswers[]
): number => {
  if (questions.length === 0) return 0;

  const answeredCount = questions.reduce((count, question, index) => {
    const answer = currentAnswers[index];
    return isQuestionAnswered(question, answer) ? count + 1 : count;
  }, 0);

  return Math.round((answeredCount / questions.length) * 100);
};

export const formatTimeRemaining = (milliseconds: number): string => {
  if (milliseconds <= 0) return "0s";

  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

export const createDefaultQuestionAnswer = (
  questionId: string,
  questionType: string = QUESTION_TYPES.DESCRIPTIVE
): QuestionAnswers => {
  return {
    questionId,
    answer: questionType === QUESTION_TYPES.FILE_UPLOAD ? null : null,
    fileUrl: questionType === QUESTION_TYPES.FILE_UPLOAD ? null : null,
  };
};

export const sanitizeInput = (input: string): string => {
  if (!input) return "";

  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/javascript:/gi, "")
    .replace(/on\w+\s*=/gi, "");
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const supportsFullscreen = (): boolean => {
  return !!(
    document.fullscreenEnabled ||
    (document as any).webkitFullscreenEnabled ||
    (document as any).mozFullScreenEnabled ||
    (document as any).msFullscreenEnabled
  );
};

export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

export const getQuestionTitle = (question: Question): string => {
  if (!question) return "Unknown Question";

  const title = question.description || "Untitled Question";
  return title.length > 50 ? `${title.substring(0, 50)}...` : title;
};

export const createInterviewError = (
  message: string,
  code?: string,
  context?: any
): Error & { code?: string; context?: any } => {
  const error = new Error(message) as Error & { code?: string; context?: any };
  if (code) error.code = code;
  if (context) error.context = context;
  return error;
};

export const isAnswered = (aiAssessmentQuestion: AIAssessmentQuestion) => {
  return Boolean(
    aiAssessmentQuestion.answer && aiAssessmentQuestion.answer.trim().length > 0
  );
};

export const groupActivityLogs = (
  activityLogs: InterviewSessionActivityLog[]
) => {
  const grouped = activityLogs.reduce((acc, log) => {
    if (!acc[log.event]) {
      acc[log.event] = { count: 0, lastOccurred: "" };
    }

    acc[log.event].count += 1;

    if (
      !acc[log.event].lastOccurred ||
      dayjs(log.timestamp).isAfter(dayjs(acc[log.event].lastOccurred))
    ) {
      acc[log.event].lastOccurred = dayjs(log.timestamp).toISOString();
    }

    return acc;
  }, {} as Record<string, { count: number; lastOccurred: string } & any>);

  return Object.entries(grouped).map(([event, data]) => ({
    event,
    count: data.count,
    lastOccurred: dayjs(data.lastOccurred).format("DD MMM YYYY hh:mm A"),
  }));
};

export const getActivityLogEventTitleByEvent = (event: string) => {
  switch (event) {
    case "fullscreen_exit":
      return "Fullscreen Exit";
    case "keyboard_shortcut":
      return "Keyboard Shortcut";
    case "cursor_focus_switch":
      return "Mouse Cursor Focus Change";
    case "multiple_tabs":
      return "Multiple Tabs";
    case "refresh_page":
      return "Refresh Page";
    case "screen_recording_stopped":
      return "Screen Recording Stopped";
    case "missed_ping_limit_reached":
      return "Connection Interruption";
    case "session_expired":
      return "Session Expired";
    case "fullscreen_exit_attempt":
      return "Fullscreen Exit Attempt";
    case "camera_covering":
      return "Camera Obstruction";
    default:
      return `Violation: ${event || "Unknown Event"}`;
  }
};

export const getActivityLogEventDescriptionByEventAndCount = (
  event: string,
  count: number
) => {
  switch (event) {
    case "fullscreen_exit":
      return "You exited full-screen mode during the AI Assessment Round.";
    case "keyboard_shortcut":
      return `Tried to use keyboard shortcuts during the AI Assessment Round ${count} ${
        count === 1 ? "time" : "times"
      }.`;
    case "cursor_focus_switch":
      return `Tried to switch mouse cursor focus during the AI Assessment Round ${count} ${
        count === 1 ? "time" : "times"
      }.`;
    case "multiple_tabs":
      return `Tried to switch to multiple tabs during the AI Assessment Round ${count} ${
        count === 1 ? "time" : "times"
      }.`;
    case "refresh_page":
      return "Page was refreshed during the AI Assessment Round.";
    case "screen_recording_stopped":
      return "Screen recording was stopped during the AI Assessment Round.";
    case "missed_ping_limit_reached":
      return `Your internet connection was interrupted or unstable during the AI Assessment Round ${
        count > 1 ? `(${count} interruptions)` : ""
      }.`;
    case "session_expired":
      return "The session has expired during the AI Assessment Round.";
    case "fullscreen_exit_attempt":
      return `Attempted to exit full-screen mode during the AI Assessment Round ${count} ${
        count === 1 ? "time" : "times"
      }.`;
    case "camera_covering":
      return `Camera was obstructed or covered during the AI Assessment Round ${count} ${
        count === 1 ? "time" : "times"
      }.`;
    default:
      return `A violation was detected during the AI Assessment Round${
        count > 1 ? ` (occurred ${count} times)` : ""
      }.`;
  }
};

export const getActivityLogEventIconByEvent = (
  event: string
): React.ReactNode => {
  switch (event) {
    case "fullscreen_exit":
      return React.createElement(FullscreenExitOutlined);
    case "keyboard_shortcut":
      return React.createElement(DeleteRowOutlined);
    case "cursor_focus_switch":
      return React.createElement(SendOutlined);
    case "multiple_tabs":
      return React.createElement(BlockOutlined);
    case "refresh_page":
      return React.createElement(ReloadOutlined);
    case "screen_recording_stopped":
      return React.createElement(VideoCameraOutlined);
    case "missed_ping_limit_reached":
      return React.createElement(DisconnectOutlined);
    case "session_expired":
      return React.createElement(ClockCircleOutlined);
    case "fullscreen_exit_attempt":
      return React.createElement(FullscreenOutlined);
    case "camera_covering":
      return React.createElement(CameraOutlined);
    default:
      return React.createElement(WarningOutlined);
  }
};

export const getBrowserFingerPrint = async () => {
  const { hash } = await getFingerprint(true);
  return hash;
};
